import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2, AlertCircle, FileText } from "lucide-react";

interface ProposalStepProps {
  includeSections: {
    executiveSummary: boolean;
    problemStatement: boolean;
    solutionMapping: boolean;
    roiAnalysis: boolean;
    pricingTerms: boolean;
  };
  onToggleSection: (section: keyof ProposalStepProps['includeSections']) => void;
  isGenerating: boolean;
  onGenerateProposals: () => void;
  generatedCount: number;
  selectedLeadCount: number;
  error?: string;
}

export function ProposalStep({
  includeSections,
  onToggleSection,
  isGenerating,
  onGenerateProposals,
  generatedCount,
  selectedLeadCount,
  error
}: ProposalStepProps) {
  const sections = [
    { id: 'executiveSummary' as const, label: "Executive Summary" },
    { id: 'problemStatement' as const, label: "Problem Statement & Pain Points" },
    { id: 'solutionMapping' as const, label: "Solution Mapping" },
    { id: 'roiAnalysis' as const, label: "ROI Analysis & Business Case" },
    { id: 'pricingTerms' as const, label: "Pricing & Terms" },
  ];

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground">
        AI will generate complete proposals for {selectedLeadCount} qualified leads.
      </p>
      
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      
      <Card>
        <CardContent className="p-4 space-y-3">
          <Label>Include Sections</Label>
          {sections.map((section, i) => (
            <div key={section.id} className="flex items-center gap-3">
              <Checkbox 
                id={section.id}
                checked={includeSections[section.id]}
                onCheckedChange={() => onToggleSection(section.id)}
              />
              <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-medium">
                {i + 1}
              </div>
              <Label htmlFor={section.id} className="cursor-pointer flex-1">{section.label}</Label>
              <Sparkles className="h-3 w-3 text-primary" />
            </div>
          ))}
        </CardContent>
      </Card>
      
      <Button 
        onClick={onGenerateProposals} 
        disabled={isGenerating || selectedLeadCount === 0}
        className="w-full"
      >
        {isGenerating ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Generating Proposals...
          </>
        ) : (
          <>
            <FileText className="h-4 w-4 mr-2" />
            Generate {selectedLeadCount} Proposals
          </>
        )}
      </Button>
      
      {generatedCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg animate-fade-in">
          <FileText className="h-4 w-4" />
          <span className="text-sm">{generatedCount} proposals generated successfully</span>
        </div>
      )}
      
      <div className="text-center text-sm text-muted-foreground">
        Proposals are generated in under 10 seconds with AI
      </div>
    </div>
  );
}
